/**
 * The draw from a lesson, inside the Mystic's real session (lesson-redo
 * DESIGN.md E2; API.md 6.1, 6.4). The picker is SelectCards on the
 * talk_pull_card path and the card is TalkCard; what is new is how the
 * session starts (how=draw | talk) and the one next step after the card
 * (since 2026-10-28: "Go deeper", API.md 6.4 v1.6).
 */

const LESSON_CHIPS = ['lesson_full_reading', 'lesson_change_subject']

/** The row after the card (its one step is ``lessonOneStep``). */
export function isLessonToolRow(actions) {
  return Array.isArray(actions) && actions.some((a) => a && LESSON_CHIPS.includes(a.action_type))
}

/**
 * One screen, one job, one next step (Chris, 2026-10-28; API.md 6.4 v1.6):
 * after the card only "Go deeper" is offered. An older server's Talk it
 * through and Change the subject are left out here (their paths still work
 * elsewhere); a Mystic with nothing to sell leaves the talk as the one step.
 */
export function lessonOneStep(actions) {
  const list = Array.isArray(actions) ? actions.filter(Boolean) : []
  const full = list.find((a) => a.action_type === 'lesson_full_reading')
  if (full) return [full]
  const talk = list.find((a) => a.action_type === 'chat_about_this')
  return talk ? [talk] : list.filter((a) => a.action_type !== 'lesson_change_subject')
}

/** "Go deeper with {Mystic}": the Mystic's name when the chip carries it. */
export function goDeeperLabel(action) {
  const name = String((action && action.reader_name) || '').trim()
  if (name) return `Go deeper with ${name}`
  const label = String((action && action.button_label) || '').trim()
  return /^get the full reading$/i.test(label) ? 'Go deeper' : label
}

/** ✦ Go deeper with Mystic Aida · 50 ✦ (the last ✦ is the carat mark). */
export function lessonChipLabel(action) {
  if (!action || action.action_type !== 'lesson_full_reading') return String((action && action.button_label) || '').trim()
  const price = Number(action.price_carats) || 0
  return price > 0 ? `✦ ${goDeeperLabel(action)} · ${price} ✦` : `✦ ${goDeeperLabel(action)} ✦`
}

/**
 * The chip's label as parts for the template: the opening ✦ is an ornament,
 * the closing one the price's carat mark ({ carat }); with prices hidden (or
 * no price) both are ornaments.
 */
export function lessonChipSegments(action, { pricesVisible = true } = {}) {
  if (!action || action.action_type !== 'lesson_full_reading') return [{ text: String((action && action.button_label) || '') }]
  const price = Number(action.price_carats) || 0
  if (pricesVisible && price > 0) return [{ orn: true }, { text: `${goDeeperLabel(action)} · ${price}` }, { carat: true }]
  return [{ orn: true }, { text: goDeeperLabel(action) }, { orn: true }]
}

/** Only the gold fill marks the first. */
export const isLessonPrimary = (action) => !!action && action.action_type === 'lesson_full_reading'

/** start-session's `how`: only with the deliberate ask from this lesson (held in memory, never a reload). */
export function lessonHowParam(from, askFor, how) {
  if (!from || !askFor || String(from) !== String(askFor)) return ''
  return how === 'draw' || how === 'talk' ? `&how=${how}` : ''
}

const RESUMED_READING_STATES = ['TAROT_N_CARD_DRAW', 'GAME_TAROT_4_CARDS']

/**
 * The welcome landed in a card-draw state: is that a finished reading coming
 * back (Continue Reading, a reload), whose spread the page mounts itself?
 * Not when the session is a lesson's draw or talk (API.md 6.2: the state is
 * TAROT_N_CARD_DRAW with no reading yet), and not when the picker is already
 * up (resume-card-selection armed it): mounting "the reading" there closed the
 * picker and showed "Weaving your reading…" over nothing (walk B1, B5).
 */
export function mountsResumedReading(state, { lessonHow = '', pickerArmed = false } = {}) {
  if (!RESUMED_READING_STATES.includes(state)) return false
  if (lessonHow === 'draw' || lessonHow === 'talk') return false
  return !pickerArmed
}

/**
 * "Get the full reading" from a lesson lays out Before · Now · Ahead · What
 * helps (DESIGN.md E2.7), the words the Mystic says over it. The server's own
 * names win when it sends them for every slot; until then the spread's
 * Past/Present/Future/Advice are put in those words here, as the server's
 * lesson_draw._positions does, and a Mystic's own names are kept.
 * Null when there is nothing to say: the spread's labels stand.
 */
export const LESSON_POSITION_WORDS = ['Before', 'Now', 'Ahead', 'What helps']
const WORD_FOR = { past: 'Before', present: 'Now', future: 'Ahead', advice: 'What helps' }
const bare = (label) => String(label || '').trim().toLowerCase().replace(/^(the|tarot)\s+/, '')

export function lessonSlotLabels({ given, positions, count } = {}) {
  const n = Number(count) || 0
  const sent = Array.isArray(given) ? given.map((l) => String(l || '').trim()) : []
  if (n > 0 && sent.length === n && sent.every(Boolean)) return sent
  const own = (Array.isArray(positions) ? positions : [])
    .map((p) => String((p && (p.label || p.key)) || '').trim())
    .filter(Boolean)
  if (n > 0 && own.length === n) return own.map((l) => WORD_FOR[bare(l)] || l)
  return n === 4 ? [...LESSON_POSITION_WORDS] : null
}

/** The names the chip (or a picker frame) carries for the full reading's slots, if any. */
export function lessonLabelsOf(payload) {
  const p = payload || {}
  const l = p.position_labels || p.slot_labels || p.positions
  return Array.isArray(l) ? l.map((x) => (x && typeof x === 'object' ? x.label : x)) : null
}

/**
 * "Get the full reading" as the conversation reply tells it (API.md 6.4,
 * v1.5): `lesson_full` = { reading_session_id, is_paid, charged,
 * carat_balance, price_carats, pay_at: start | reveal, opens: interview },
 * or { short } (the usual 402 body: nothing started, nothing charged).
 * It starts a regular reading: the Mystic's one hand-off line and the
 * interview, the composer open (Chris, 2026-10-28); no slot names now.
 * Null when the reply has none.
 */
export function readLessonFull(lf) {
  if (!lf || typeof lf !== 'object') return null
  if (lf.short) return { short: lf.short }
  return {
    short: null,
    readingId: String(lf.reading_session_id || ''),
    isPaid: lf.is_paid === true,
    charged: Math.max(0, Number(lf.charged) || 0),
    balance: typeof lf.carat_balance === 'number' ? lf.carat_balance : null,
    price: Number(lf.price_carats) || 0,
    payAt: lf.pay_at === 'reveal' ? 'reveal' : 'start',
    opens: lf.opens === 'interview' ? 'interview' : '',
    labels: lessonLabelsOf(lf),
  }
}

/** The full reading started: the composer opens on the Mystic's interview. */
export function lessonFullOpens(full) {
  return !!(full && !full.short && full.opens === 'interview')
}

/**
 * The new reading's paid state from its start (Mystic Aida's is sealed until
 * Reveal, whatever the draw before it was), and the balance: a step down by
 * what was taken (`onStep(charged)`, for the "−N ✦"), then the server's figure.
 * A short reply only carries the server's balance; nothing else changes.
 */
export function applyLessonFull(store, full, { onStep } = {}) {
  if (!store || !full) return
  if (full.short) {
    if (typeof full.short.carat_balance === 'number') store.querent_carat_balance = full.short.carat_balance
    return
  }
  if (full.readingId) store.latest_reading_session = full.readingId
  store.latest_reading_session_is_paid = full.isPaid
  const known = typeof store.querent_carat_balance === 'number' && store.querent_carat_balance_loaded !== false
  if (full.charged > 0) {
    if (known) store.querent_carat_balance = Math.max(0, store.querent_carat_balance - full.charged)
    if (onStep) onStep(full.charged)
  }
  if (full.balance !== null) store.querent_carat_balance = full.balance
}

/**
 * The screen as it was, to put back: "Get the full reading" arms the picker
 * as the turn goes, and a `lesson_full.short` reply means nothing started,
 * so the picker it armed closes onto what was there (API.md 6.4).
 */
export function holdRefs(refs) {
  const kept = Object.values(refs || {}).map((r) => [r, r.value])
  return () => { kept.forEach(([r, v]) => { r.value = v }) }
}

/**
 * The welcome of a lesson's draw or talk (API.md 6.2): `lesson_draw`
 * { opener, card_question, price_carats, picker | card, resumed } or
 * `lesson_talk` { opener, resumed }. `resumed` is a reload (v1.4): the
 * server restores what was there; nothing new is drawn or charged.
 * Null for any other welcome.
 */
export function lessonWelcomeOf(data) {
  const d = data || {}
  const draw = d.lesson_draw && typeof d.lesson_draw === 'object' ? d.lesson_draw : null
  const talk = !draw && d.lesson_talk && typeof d.lesson_talk === 'object' ? d.lesson_talk : null
  const w = draw || talk
  if (!w) return null
  return {
    how: draw ? 'draw' : 'talk',
    resumed: w.resumed === true,
    opener: String(w.opener || ''),
    cardQuestion: String(w.card_question || ''),
    price: Number(w.price_carats) || 0,
    picker: (draw && draw.picker) || null,
    card: (draw && draw.card) || null,
    quickActions: Array.isArray(d.quick_actions) ? d.quick_actions : [],
  }
}

/**
 * Is this `talk-card` frame the card drawn from a lesson ("Your card", E2.5)?
 * Its own flag; the pick just made (live, not yet read); or the first card a
 * reload of the draw replays (resume: the replayed frame carries no flag).
 */
export function isLessonCard(card, { filled = false, live = false, resume = false } = {}) {
  return !!((card && card.lesson_draw) || (!filled && live) || resume)
}

/**
 * A reply for a reading this screen has left (API.md 6.6, v1.4): the server
 * says `moved_on` (a Talk it through turn, or a lesson's card read for most
 * of a minute, while the next draw opened), or the session it was sent on is
 * no longer this one. Dropped: the screen stays as it is.
 */
export function isLateReply(data, { sentSession = '', session = '' } = {}) {
  if (data && data.moved_on === true) return true
  return !!(sentSession && session && String(sentSession) !== String(session))
}
